<template>
  <accordion-panel
    id="oscillography-parameters"
    class="oscillography-parameters-tab"
  >
    <div>
      <oscillography-analysis-parameters-diagnostic-limits-table
        :limits="diagnosticLimits"
        :invalid-limit-keys="invalidDiagnosticLimitKeys"
        :edit-mode="editMode"
        :can-edit="canEdit"
        @restore="restoreDefaults"
      />

      <div
        v-if="canEdit"
        class="oscillography-parameters-tab__actions no-internal-print"
      >
        <b-button
          v-if="editMode"
          class="mr-2"
          variant="secondary"
          @click="confirmCancel"
        >
          {{ $t('actions.cancel') }}
        </b-button>
        <b-button
          v-if="editMode"
          variant="primary"
          :disabled="isSaveBlocked"
          @click="confirmEdit"
        >
          {{ $t('actions.save') }}
        </b-button>
        <b-button
          v-else
          variant="primary"
          @click="enterEditMode"
        >
          {{ $t('actions.edit') }}
        </b-button>
      </div>
    </div>
  </accordion-panel>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import OscillographyService from '@/domain/services/installation/engineering/oscillographyAnalysis/oscillographyAnalysisService';
import cloneDeep from 'lodash/cloneDeep';
import OscillographyAnalysisParametersDiagnosticLimitsTable from './OscillographyAnalysisParametersDiagnosticLimitsTable';

export default {
  name: 'OscillographyAnalysisParametersTab',
  components: {
    AccordionPanel,
    OscillographyAnalysisParametersDiagnosticLimitsTable,
  },
  props: {
    assetModuleActiveIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    diagnosticLimits: [],
    diagnosticLimitsBackup: [],
    editMode: false,
  }),
  computed: {
    canEdit() {
      return this.$hasUserPermission('PER033');
    },
    invalidDiagnosticLimitKeys() {
      if (!this.editMode) return [];

      return this.diagnosticLimits
        .filter(limit => this.isDiagnosticLimitRangeInvalid(limit))
        .map(({ variable, maneuverType }) => `${variable}-${maneuverType}`);
    },
    hasInvalidDiagnosticLimits() {
      return this.invalidDiagnosticLimitKeys.length > 0;
    },
    isSaveBlocked() {
      return this.hasMissingLowerLimit() || this.hasInvalidDiagnosticLimits;
    },
  },
  watch: {
    hasInvalidDiagnosticLimits(isInvalid, wasInvalid) {
      if (isInvalid && !wasInvalid) {
        this.$pushAlert({ code: 'ME101' });
      }
    },
    assetModuleActiveIds: {
      handler(moduleActiveIds) {
        if (moduleActiveIds.length) {
          this.loadParameters();
        }
      },
      deep: true,
    },
  },
  created() {
    this.loadParameters();
  },
  methods: {
    getTargetModuleIds() {
      const moduleIds = this.assetModuleActiveIds.filter(Boolean);

      if (moduleIds.length) {
        return [...new Set(moduleIds)];
      }

      const { assetModuleId } = this.$route.params;
      return assetModuleId ? [assetModuleId] : [];
    },
    loadParameters() {
      const [assetModuleId] = this.getTargetModuleIds();

      if (!assetModuleId) return;

      OscillographyService.getDiagnosticLimits(assetModuleId).then((response) => {
        this.diagnosticLimits = cloneDeep(response);
      });
    },
    enterEditMode() {
      this.diagnosticLimitsBackup = cloneDeep(this.diagnosticLimits);
      this.editMode = true;
    },
    restoreDefaults() {
      const [assetModuleId] = this.getTargetModuleIds();

      if (!assetModuleId) return;

      if (!this.editMode) {
        this.diagnosticLimitsBackup = cloneDeep(this.diagnosticLimits);
      }
      this.editMode = true;
      OscillographyService.getDefaultDiagnosticLimits(assetModuleId).then((response) => {
        this.diagnosticLimits = cloneDeep(response);
      });
    },
    confirmCancel() {
      this.$confirmMessage({ code: 'MC006' }).accepted(() => {
        this.cancelEdit();
      });
    },
    cancelEdit() {
      this.diagnosticLimits = cloneDeep(this.diagnosticLimitsBackup);
      this.diagnosticLimitsBackup = [];
      this.editMode = false;
    },
    confirmEdit() {
      if (this.isSaveBlocked) return;

      this.$confirmMessage({ code: 'MC011' }).accepted(() => {
        this.saveParameters();
      });
    },
    saveParameters() {
      if (this.isSaveBlocked) return;

      const moduleIds = this.getTargetModuleIds();

      Promise.all(moduleIds.map(assetModuleId => (
        OscillographyService.putDiagnosticLimits(assetModuleId, this.diagnosticLimits)
      ))).then(() => {
        this.loadParameters();
        this.diagnosticLimitsBackup = [];
        this.editMode = false;
        this.$pushAlert({ code: 'MS021' });
        this.$emit('parameters-saved');
      });
    },
    hasMissingLowerLimit() {
      return this.diagnosticLimits.some(({ lowerLimit }) => (
        lowerLimit === null
        || lowerLimit === undefined
        || (typeof lowerLimit === 'string' && lowerLimit.trim() === '')
      ));
    },
    isDiagnosticLimitRangeInvalid({ lowerLimit, upperLimit }) {
      const lowerValue = this.parseLimitValue(lowerLimit);
      const upperValue = this.parseLimitValue(upperLimit);

      return lowerValue !== null
        && upperValue !== null
        && lowerValue >= upperValue;
    },
    parseLimitValue(value) {
      if (value === null || value === undefined) return null;

      if (typeof value === 'string' && value.trim() === '') return null;

      const parsedValue = typeof value === 'number'
        ? value
        : Number(String(value).trim().replace(',', '.'));

      return Number.isFinite(parsedValue) ? parsedValue : null;
    },
  },
};
</script>

<style lang="sass" scoped>
.oscillography-parameters-tab
  &__actions
    display: flex
    justify-content: flex-end
    margin-top: 1.5rem
  ::v-deep .accordion-panel__header
    display: none
  ::v-deep .accordion-panel__clickable-area
    display: none
  ::v-deep .accordion-panel__right-controls
    display: none
  ::v-deep .accordion-panel__title-container
    flex: 0 0 100%
    max-width: 100%
  ::v-deep .accordion-panel__header
    cursor: default
</style>
